import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { scrollToElement } from '../../utils/scrollUtils';
import { useContactInfo } from '../../utils/contactObfuscation';
import { headerSocialLinks } from '../../data/socialLinks';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const contactInfo = useContactInfo();

  const navigationItems = [
    { 
      label: 'HOME', 
      path: '/', 
      icon: 'Home'
    },
    { 
      label: 'PRODUCTS', 
      path: '/product', 
      icon: 'Package',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Email Validator SaaS',
          description: 'Professional email validation service with 99.9% accuracy. Validate single emails or bulk lists to reduce bounce rates.',
          link: 'https://mailvalidator.octalchip.com/',
          icon: 'Mail'
        },
        {
          title: 'QuickSite',
          description: 'Done-for-you business website with hosting, SSL, templates, and WhatsApp-led domain setup with a clear fast launch plan.',
          link: 'https://quicksite.octalchip.com/',
          icon: 'LayoutTemplate'
        }
      ]
    },
    { 
      label: 'SERVICES', 
      path: '/services', 
      icon: 'Settings',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Generative AI Integration',
          description: 'Ship generative AI, copilots, and LLM features into your product stack.',
          link: '/services/ai-integration',
          icon: 'Sparkles'
        },
        {
          title: 'AI Workflow Automation',
          description: 'Combine n8n, Zapier, Make, and LLM agents to remove repetitive work.',
          link: '/services/workflow-automation',
          icon: 'Zap'
        },
        {
          title: 'AI-Native Web Products',
          description: 'Build AI-native web apps and SaaS experiences that convert.',
          link: '/services/web-development',
          icon: 'Globe'
        },
        {
          title: 'Cloud AI & LLMOps',
          description: 'Deploy and scale AI workloads with secure cloud, CI/CD, and LLMOps.',
          link: '/services/cloud-devops',
          icon: 'Cloud'
        },
        {
          title: 'AI-Powered Mobile Apps',
          description: 'Launch mobile apps with AI assistants, personalization, and retention loops.',
          link: '/services/mobile-app-development',
          icon: 'Smartphone'
        },
        {
          title: 'AI Backend & Orchestration',
          description: 'Build APIs and orchestration layers that power agents and RAG systems.',
          link: '/services/backend-development',
          icon: 'Server'
        },
        {
          title: 'AI Product UX',
          description: 'Design AI product UX users trust: streaming answers, citations, and clear handoffs.',
          link: '/services/ui-ux-design',
          icon: 'LayoutTemplate'
        },
        {
          title: 'AI-Assisted Growth Automation',
          description: 'Scale pipeline with AI-assisted WhatsApp, LinkedIn, RCS, and email automation.',
          link: '/services/marketing-services',
          icon: 'Megaphone'
        }
      ]
    },
    { 
      label: 'AI SERVICES', 
      path: '/ai-ml-services', 
      icon: 'Sparkles',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'AI Agents & Copilots',
          description: 'Build tool-using agents and copilots that complete multi-step work safely.',
          link: '/services/ai-ml-services/ai-chatbots',
          icon: 'Bot'
        },
        {
          title: 'RAG & Knowledge AI',
          description: 'Ground LLMs on your docs and systems with retrieval, citations, and evals.',
          link: '/services/ai-ml-services/natural-language-processing',
          icon: 'Database'
        },
        {
          title: 'AI Consulting',
          description: 'Turn GenAI ideas into a ROI roadmap and a 90-day implementation plan.',
          link: '/services/ai-ml-services/ai-consulting',
          icon: 'Lightbulb'
        },
        {
          title: 'Computer Vision',
          description: 'Detect defects, read documents, and automate visual checks with multimodal AI.',
          link: '/services/ai-ml-services/computer-vision',
          icon: 'Eye'
        },
        {
          title: 'Predictive Analytics',
          description: 'Forecast demand and risk faster, then expose insights through AI interfaces.',
          link: '/services/ai-ml-services/predictive-analytics',
          icon: 'TrendingUp'
        },
        {
          title: 'LLMOps & Deep Learning',
          description: 'Productionize models with evals, monitoring, guardrails, and secure inference.',
          link: '/services/ai-ml-services/deep-learning',
          icon: 'Shield'
        },
        {
          title: 'Machine Learning',
          description: 'Custom ML models that improve forecasting and automated decision quality.',
          link: '/services/ai-ml-services/machine-learning',
          icon: 'Brain'
        },
        {
          title: 'Data Science',
          description: 'Turn raw data into clear actions that improve revenue efficiency.',
          link: '/services/ai-ml-services/data-science',
          icon: 'BarChart'
        },
        {
          title: 'Reinforcement Learning',
          description: 'Train adaptive agents for optimization, robotics, and dynamic decision systems.',
          link: '/services/ai-ml-services/reinforcement-learning',
          icon: 'Network'
        }
      ]
    },
    { 
      label: 'BLOG', 
      path: '/blog', 
      icon: 'BookOpen'
    },
    { 
      label: 'HIRE AI', 
      path: '/services#pricing', 
      icon: 'Users',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Hourly Engagement',
          description: 'Scale delivery this week with flexible experts when deadlines are tight.',
          link: '/services#pricing'
        },
        {
          title: 'Monthly Dedicated',
          description: 'Get a dedicated team that ships consistently and reduces hiring delay.',
          link: '/services#pricing'
        },
        {
          title: 'Fixed Cost Projects',
          description: 'Lock scope, budget, and timeline for predictable project outcomes.',
          link: '/services#pricing'
        }
      ]
    },
    { 
      label: 'ABOUT', 
      path: '/about', 
      icon: 'Users'
    },
    { 
      label: 'CONTACT', 
      path: '/contact', 
      icon: 'Mail'
    }
  ];

  const isActiveRoute = (path) => {
    // If path has a hash, check both pathname and hash
    if (path.includes('#')) {
      const [pathWithoutHash, hash] = path.split('#');
      return location.pathname === pathWithoutHash && location.hash === `#${hash}`;
    }
    // If path doesn't have a hash, only check pathname (ignore hash in URL)
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setOpenMobileDropdown(null);
  };

  const toggleMobileDropdown = (itemLabel) => {
    setOpenMobileDropdown(openMobileDropdown === itemLabel ? null : itemLabel);
  };

  // Position fixed mega-menus under the nav label (PRODUCTS / SERVICES / AI SERVICES)
  const positionHeaderDropdown = (triggerEl, dropdownWidth = 600, align = 'center') => {
    if (!triggerEl || typeof window === 'undefined') return;
    const rect = triggerEl.getBoundingClientRect();
    const width = Math.min(dropdownWidth, window.innerWidth * 0.92);
    let leftPx;
    if (align === 'left') {
      // Sit directly under the label; shift left only if it would overflow the viewport
      leftPx = rect.left;
      if (leftPx + width > window.innerWidth - 8) {
        leftPx = Math.max(8, window.innerWidth - width - 8);
      }
      if (leftPx < 8) leftPx = 8;
      document.documentElement.style.setProperty('--header-dropdown-left', `${leftPx}px`);
      document.documentElement.style.setProperty('--header-dropdown-transform', 'translateX(0)');
    } else {
      const centerX = rect.left + rect.width / 2;
      const half = width / 2;
      leftPx = Math.min(
        Math.max(centerX, half + 8),
        window.innerWidth - half - 8
      );
      document.documentElement.style.setProperty('--header-dropdown-left', `${leftPx}px`);
      document.documentElement.style.setProperty('--header-dropdown-transform', 'translateX(-50%)');
    }
  };

  const openDesktopMenu = (label, triggerEl, dropdownWidth) => {
    const align = label === 'PRODUCTS' ? 'left' : 'center';
    positionHeaderDropdown(triggerEl, dropdownWidth, align);
    setOpenDesktopDropdown(label);
  };

  const getMegaMenuWidth = (label) => {
    if (label === 'SERVICES' || label === 'AI SERVICES') {
      if (typeof window !== 'undefined' && window.innerWidth >= 1536) return 1100;
      if (typeof window !== 'undefined' && window.innerWidth >= 1280) return 1000;
      return Math.min(1000, window.innerWidth * 0.92);
    }
    if (label === 'PRODUCTS') {
      if (typeof window !== 'undefined' && window.innerWidth >= 1536) return 650;
      if (typeof window !== 'undefined' && window.innerWidth >= 1280) return 600;
      return Math.min(600, window.innerWidth * 0.92);
    }
    return 320;
  };

  const scrollToSection = async (sectionId) => {
    await scrollToElement(sectionId, { 
      smooth: true,
      maxRetries: 15,
      retryDelay: 150
    }).catch(() => {
      // Silent fail - scrollToElement handles retries internally
    });
    setIsMobileMenuOpen(false);
    setOpenMobileDropdown(null);
    setOpenDesktopDropdown(null);
  };

  // Close dropdowns when clicking outside; keep mega-menu aligned on resize
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDesktopDropdown && !event.target.closest('.relative.group')) {
        setOpenDesktopDropdown(null);
      }
    };

    const handleResize = () => {
      if (!openDesktopDropdown) return;
      const group = Array.from(document.querySelectorAll('header .relative.group')).find((el) => {
        const link = el.querySelector('a');
        return link && link.textContent.trim() === openDesktopDropdown;
      });
      if (group) {
        const align = openDesktopDropdown === 'PRODUCTS' ? 'left' : 'center';
        positionHeaderDropdown(group, getMegaMenuWidth(openDesktopDropdown), align);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [openDesktopDropdown]);

  return (
    <header
      className="bg-surface border-b border-border fixed left-0 right-0 w-full z-[1001] shadow-sm"
      style={{ top: 'var(--banner-height, 64px)' }}
    >
      {/* Top Bar with Contact Info and Social Media — hidden on mobile to free viewport (phone via FREE CALL) */}
      <div className="hidden sm:block bg-[#00d690] text-white py-1.5 sm:py-2 md:py-2.5">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
          <div className="flex items-center justify-between gap-2 sm:gap-3 md:gap-4 min-w-0">
            {/* Contact Information */}
            <div className="flex flex-nowrap items-center justify-start gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm md:text-base min-w-0 overflow-hidden">
              {(contactInfo.phoneLink || contactInfo.phone) && (
              <div className="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0">
                <Icon name="Phone" size={16} className="text-white sm:w-[18px] sm:h-[18px] flex-shrink-0" />
                {contactInfo.phoneLink ? (
                  <a href={contactInfo.phoneLink} className="hover:text-white/80 transition-colors font-medium whitespace-nowrap">
                    {contactInfo.phone}
                  </a>
                ) : (
                  <span className="font-medium whitespace-nowrap">{contactInfo.phone}</span>
                )}
              </div>
              )}
              {contactInfo.mailtoLink && (
                <div className="hidden lg:flex items-center space-x-1.5 sm:space-x-2 min-w-0">
                  <Icon name="Mail" size={16} className="text-white sm:w-[18px] sm:h-[18px] flex-shrink-0" />
                  <a href={contactInfo.mailtoLink} className="hover:text-white/80 transition-colors font-medium whitespace-nowrap">
                    {contactInfo.email}
                  </a>
                </div>
              )}
            </div>

            {/* Social Media Icons */}
            <div className="flex items-center justify-end flex-shrink-0">
              <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 lg:gap-2.5">
                {headerSocialLinks.map((social, index) => (
                  <a 
                    key={index}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 lg:w-10 lg:h-10 rounded-full text-white hover:text-white hover:bg-white/10 active:bg-white/20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 flex-shrink-0"
                    aria-label={social.name}
                  >
                    <Icon name={social.icon} size={16} className="sm:w-[18px] sm:h-[18px] md:w-5 md:h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="bg-[#f4faf8] relative border-b border-gray-200">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12 relative">
          <div className="flex items-center h-12 sm:h-14 md:h-16 lg:h-[72px] gap-2 sm:gap-3 md:gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0 min-w-0">
              <img 
                src="/octalchiplogo.svg" 
                alt="OctalChip - AI Engineering Company Logo - Generative AI, Agents, RAG & LLM Solutions" 
                className="h-5 sm:h-6 md:h-6 lg:h-7 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-x-1 xl:gap-x-2 2xl:gap-x-4 flex-1 justify-center min-w-0 mx-1 lg:mx-2 xl:mx-3">
              {navigationItems.map((item) => (
                <div 
                  key={item.path} 
                  className="relative group flex items-center"
                  onMouseEnter={(e) => {
                    if (!item.hasDropdown) return;
                    openDesktopMenu(item.label, e.currentTarget, getMegaMenuWidth(item.label));
                  }}
                  onMouseLeave={() => setOpenDesktopDropdown(null)}
                >
                  <Link
                    to={item.path}
                    onClick={(e) => {
                      if (item.path.startsWith('/#')) {
                        e.preventDefault();
                        scrollToSection(item.path.substring(2));
                      }
                      // If it has a dropdown, allow navigation to the page
                      // The dropdown will be controlled by the arrow button only
                    }}
                    className={`flex items-center space-x-0.5 text-[11px] xl:text-sm 2xl:text-base font-medium whitespace-nowrap transition-all duration-200 pb-1 border-b-2 ${
                      isActiveRoute(item.path) 
                        ? 'text-[#00d690] border-[#00d690]' 
                        : 'text-gray-800 hover:text-[#00d690] border-transparent hover:border-[#00d690]/50'
                    }`}
                  >
                    <span>{item.label}</span>
                  </Link>
                  {item.hasDropdown && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (openDesktopDropdown === item.label) {
                          setOpenDesktopDropdown(null);
                        } else {
                          openDesktopMenu(
                            item.label,
                            e.currentTarget.closest('.relative.group'),
                            getMegaMenuWidth(item.label)
                          );
                        }
                      }}
                      className="ml-0.5 p-0.5 xl:p-1 rounded-full hover:bg-gray-50 transition-colors flex items-center justify-center btn-animate-pulse flex-shrink-0"
                      aria-label={`Toggle ${item.label} dropdown`}
                    >
                      <Icon 
                        name={openDesktopDropdown === item.label ? 'ChevronUp' : 'ChevronDown'} 
                        size={14} 
                        className="transition-transform duration-200 text-gray-600 hover:text-[#00d690]" 
                      />
                    </button>
                  )}
                  
                  {/* Dropdown Menu */}
                  {item.hasDropdown && (
                    <div 
                      className={`${
                        item.label === 'SERVICES' || item.label === 'AI SERVICES' || item.label === 'PRODUCTS'
                          ? 'fixed'
                          : 'absolute top-full left-1/2 -translate-x-1/2'
                      } mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl transition-all duration-200 overflow-x-hidden overflow-y-auto max-h-[min(70vh,calc(100vh-var(--total-fixed-height,210px)-12px))] ${
                        openDesktopDropdown === item.label 
                          ? 'opacity-100 visible translate-y-0' 
                          : 'opacity-0 invisible -translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0'
                      } ${
                        item.label === 'SERVICES' || item.label === 'AI SERVICES' 
                          ? 'w-[min(92vw,1000px)] xl:w-[1000px] 2xl:w-[1100px]' 
                          : item.label === 'PRODUCTS' 
                          ? 'w-[min(92vw,600px)] xl:w-[600px] 2xl:w-[650px]' 
                          : 'w-[min(92vw,300px)] lg:w-[300px] xl:w-[320px]'
                      }`}
                      style={{ 
                        zIndex: 1002,
                        ...(item.label === 'SERVICES' || item.label === 'AI SERVICES' || item.label === 'PRODUCTS' ? {
                          top: 'calc(var(--total-fixed-height, 210px) + 4px)',
                          // Anchor under the trigger label (PRODUCTS left-aligned; SERVICES/AI centered)
                          left: 'var(--header-dropdown-left, 50%)',
                          transform: 'var(--header-dropdown-transform, translateX(-50%))',
                          marginTop: '0px'
                        } : {})
                      }}
                      onMouseEnter={() => setOpenDesktopDropdown(item.label)}
                      onMouseLeave={() => setOpenDesktopDropdown(null)}
                    >
                      <div className="py-2 sm:py-3 lg:py-4">
                        {item.label === 'AI SERVICES' ? (
                          // AI SERVICES dropdown with responsive layout
                          <div className="grid grid-cols-1 lg:grid-cols-4 gap-0 overflow-hidden">
                            {/* First 2 columns: AI Services */}
                            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-0 overflow-hidden">
                              {item.dropdownItems.map((dropdownItem, index) => (
                                <div key={index} className="px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-[#00d690]/5 transition-colors border-b sm:border-b-0 sm:border-r border-gray-100 last:border-b-0 min-w-0">
                                  <div className="flex items-start gap-2.5 sm:gap-3 min-w-0">
                                    <div className="flex-shrink-0 mt-0.5">
                                      <Icon name={dropdownItem.icon} size={18} className="text-[#00d690]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1 min-w-0">
                                        <h4 className="text-sm font-semibold text-gray-900 break-words min-w-0">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 py-0.5 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block flex-shrink-0 btn-animate-wiggle"
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-xs text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 py-0.5 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block flex-shrink-0 btn-animate-wiggle"
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-600 leading-snug break-words">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Last 2 columns: CTA */}
                            <div className="lg:col-span-2 bg-[#f4faf8] px-4 sm:px-5 py-5 sm:py-6 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-gray-200">
                              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
                                Turn AI Plans Into Growth
                              </h3>
                              <p className="text-sm text-gray-600 mb-4 sm:mb-5 leading-relaxed">
                                Get a practical 30-minute roadmap call focused on outcomes, ROI, and speed to launch.
                              </p>
                              <a
                                href="https://cal.com/octalchip/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#00d690] hover:bg-[#00b876] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 inline-flex items-center justify-center w-fit shadow-md hover:shadow-lg btn-animate-scale"
                              >
                                Book Free Call
                                <Icon name="ChevronRight" size={16} className="ml-2" />
                              </a>
                            </div>
                          </div>
                        ) : item.label === 'SERVICES' ? (
                          // Services dropdown with responsive layout
                          <div className="grid grid-cols-1 lg:grid-cols-4 gap-0 overflow-hidden">
                            {/* First 2 columns: Services */}
                            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-0 overflow-hidden">
                              {item.dropdownItems.map((dropdownItem, index) => (
                                <div key={index} className="px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-[#00d690]/5 transition-colors border-b sm:border-b-0 sm:border-r border-gray-100 last:border-b-0 min-w-0">
                                  <div className="flex items-start gap-2.5 sm:gap-3 min-w-0">
                                    <div className="flex-shrink-0 mt-0.5">
                                      <Icon name={dropdownItem.icon} size={18} className="text-[#00d690]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1 min-w-0">
                                        <h4 className="text-sm font-semibold text-gray-900 break-words min-w-0">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 py-0.5 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block flex-shrink-0 btn-animate-wiggle"
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-xs text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 py-0.5 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block flex-shrink-0 btn-animate-wiggle"
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-600 leading-snug break-words">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Last 2 columns: CTA */}
                            <div className="lg:col-span-2 bg-[#f4faf8] px-4 sm:px-5 py-5 sm:py-6 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-gray-200">
                              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
                                Need Production AI Faster?
                              </h3>
                              <p className="text-sm text-gray-600 mb-4 sm:mb-5 leading-relaxed">
                                Book a free AI strategy call and get a practical plan to ship agents, RAG, and copilots that convert.
                              </p>
                              <a
                                href="https://cal.com/octalchip/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#00d690] hover:bg-[#00b876] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 inline-flex items-center justify-center w-fit shadow-md hover:shadow-lg btn-animate-glow"
                              >
                                Book Free Call
                                <Icon name="ChevronRight" size={16} className="ml-2" />
                              </a>
                            </div>
                          </div>
                        ) : item.label === 'PRODUCTS' ? (
                          // Product dropdown with responsive layout
                          <div className="grid grid-cols-1 lg:grid-cols-4 gap-0">
                            {/* First 2 columns: Products */}
                            <div className="lg:col-span-2">
                              {item.dropdownItems.map((dropdownItem, index) => (
                                <div key={index} className="px-4 sm:px-5 py-3 sm:py-4 hover:bg-[#00d690]/5 transition-colors border-b border-gray-100 last:border-b-0">
                                  <div className="flex flex-col sm:flex-row items-start sm:items-start space-y-2 sm:space-y-0 sm:space-x-3">
                                    <div className="flex-shrink-0">
                                      <Icon name={dropdownItem.icon} size={20} className="text-[#00d690]" />
                                    </div>
                                    <div className="flex-1 min-w-0 w-full">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-1">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block btn-animate-rotate"
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block btn-animate-rotate"
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Last 2 columns: CTA */}
                            <div className="lg:col-span-2 bg-[#f4faf8] px-4 sm:px-6 py-6 sm:py-8 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-gray-200">
                              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                                Explore Conversion Tools
                              </h3>
                              <p className="text-sm text-gray-600 mb-4 sm:mb-5 leading-relaxed">
                                Launch faster with products built to reduce friction and increase qualified leads.
                              </p>
                              <a
                                href="https://cal.com/octalchip/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#00d690] hover:bg-[#00b876] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 inline-flex items-center justify-center w-fit shadow-md hover:shadow-lg btn-animate-float"
                              >
                                Book Free Call
                                <Icon name="ChevronRight" size={16} className="ml-2" />
                              </a>
                            </div>
                          </div>
                        ) : (
                          // Other dropdowns (Hire AI, etc.) — keep title + description visible
                          <>
                            {item.dropdownItems.map((dropdownItem, index) => (
                              <div key={index} className="px-4 sm:px-5 py-3 sm:py-4 hover:bg-[#00d690]/5 transition-colors border-b border-gray-100 last:border-b-0">
                                {dropdownItem.link.startsWith('http') ? (
                                  <a 
                                    href={dropdownItem.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block group/item"
                                  >
                                    <span className="block text-sm sm:text-base text-gray-800 group-hover/item:text-[#00d690] transition-colors font-semibold">
                                      {dropdownItem.title}
                                    </span>
                                    {dropdownItem.description && (
                                      <span className="block mt-1 text-xs sm:text-sm text-gray-600 leading-snug">
                                        {dropdownItem.description}
                                      </span>
                                    )}
                                  </a>
                                ) : (
                                  <Link 
                                    to={dropdownItem.link}
                                    className="block group/item"
                                  >
                                    <span className="block text-sm sm:text-base text-gray-800 group-hover/item:text-[#00d690] transition-colors font-semibold">
                                      {dropdownItem.title}
                                    </span>
                                    {dropdownItem.description && (
                                      <span className="block mt-1 text-xs sm:text-sm text-gray-600 leading-snug">
                                        {dropdownItem.description}
                                      </span>
                                    )}
                                  </Link>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side: CTA Button + Mobile Menu */}
            <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4 ml-auto">
              {/* CTA Button - Visible on all screens */}
              <div className="flex-shrink-0 flex items-center">
                <Button
                  variant="outline"
                  size="sm"
                  animation="glow"
                  onClick={() => window.open('https://cal.com/octalchip/30min', '_blank')}
                  className="border-primary text-primary bg-white hover:bg-primary hover:text-white transition-all duration-200 shadow-sm hover:shadow-md min-h-[36px] sm:min-h-[40px] md:min-h-[44px] active:scale-95 !px-0"
                >
                  <span className="hidden sm:inline text-xs sm:text-sm md:text-base font-semibold px-3 sm:px-4 md:px-5 whitespace-nowrap">
                    BOOK FREE CALL
                  </span>
                  <span className="sm:hidden text-[11px] font-semibold px-2.5 whitespace-nowrap">
                    FREE CALL
                  </span>
                </Button>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden flex-shrink-0 p-1.5 sm:p-2.5 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} className="text-gray-800 sm:w-6 sm:h-6" />
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="lg:hidden border-t border-gray-200 bg-white max-h-[calc(100vh-var(--total-fixed-height,168px)-8px)] overflow-y-auto overscroll-contain">
              <nav className="py-2 sm:py-3 space-y-0.5 px-2 sm:px-3">
                {navigationItems.map((item) => (
                  <div key={item.path}>
                    <div className="flex items-center">
                      <button
                        onClick={() => {
                          if (item.path.startsWith('/#')) {
                            scrollToSection(item.path.substring(2));
                          } else if (item.path.includes('#')) {
                            // Handle paths with hash (e.g., /services#pricing)
                            const [path, hash] = item.path.split('#');
                            navigate(path);
                            setIsMobileMenuOpen(false);
                            // Scroll to section after navigation
                            setTimeout(() => {
                              scrollToSection(hash);
                            }, 100);
                          } else {
                            // Navigate to the page
                            if (item.path.startsWith('http')) {
                              window.open(item.path, '_blank');
                            } else {
                              navigate(item.path);
                            }
                            setIsMobileMenuOpen(false);
                          }
                        }}
                        className={`flex items-center space-x-3 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-full flex-1 text-left transition-all duration-200 border-l-4 btn-animate-scale ${
                          isActiveRoute(item.path)
                            ? 'text-[#00d690] bg-[#00d690]/5 border-[#00d690]' 
                            : 'text-gray-700 hover:text-[#00d690] hover:bg-[#00d690]/5 border-transparent hover:border-[#00d690]/50'
                        }`}
                      >
                        <Icon name={item.icon} size={18} className="sm:w-5 sm:h-5 flex-shrink-0" />
                        <span className="whitespace-normal leading-tight">{item.label}</span>
                      </button>
                      {item.hasDropdown && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMobileDropdown(item.label);
                          }}
                          className="px-2 py-3 text-gray-700 hover:text-[#00d690] transition-colors rounded-full btn-animate-pulse"
                          aria-label={`Toggle ${item.label} dropdown`}
                        >
                          <Icon 
                            name={openMobileDropdown === item.label ? 'ChevronUp' : 'ChevronDown'} 
                            size={14} 
                          />
                        </button>
                      )}
                    </div>
                    
                    {/* Mobile Dropdown */}
                    {item.hasDropdown && openMobileDropdown === item.label && (
                      <div className="ml-8 sm:ml-10 mr-2 mt-1 bg-gray-50 rounded-md border border-gray-200">
                        {item.label === 'SERVICES' || item.label === 'AI SERVICES' ? (
                          // Services and AI SERVICES dropdown with simple single-column layout for mobile
                          <div className="px-2 sm:px-3 py-2">
                            {item.dropdownItems.map((dropdownItem, index) => (
                              <div key={index} className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 last:border-b-0">
                                {dropdownItem.link.startsWith('http') ? (
                                  <a 
                                    href={dropdownItem.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-sm sm:text-base text-gray-700 hover:text-[#00d690] transition-colors font-medium"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    {dropdownItem.title}
                                  </a>
                                ) : (
                                  <Link 
                                    to={dropdownItem.link}
                                    className="block text-sm sm:text-base text-gray-700 hover:text-[#00d690] transition-colors font-medium"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    {dropdownItem.title}
                                  </Link>
                                )}
                              </div>
                            ))}
                            {item.label === 'SERVICES' && (
                              <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-t border-gray-200 bg-white/50">
                                <Link 
                                  to="/services"
                                  className="block text-sm sm:text-base font-semibold text-[#00d690] hover:text-[#00b876] transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  View All Services →
                                </Link>
                              </div>
                            )}
                            {item.label === 'AI SERVICES' && (
                              <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-t border-gray-200 bg-white/50">
                                <Link 
                                  to="/ai-ml-services"
                                  className="block text-sm sm:text-base font-semibold text-[#00d690] hover:text-[#00b876] transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  View All AI Services →
                                </Link>
                              </div>
                            )}
                          </div>
                        ) : (
                          // Other dropdowns (Product, Hire AI, etc.)
                          <>
                            {item.dropdownItems.map((dropdownItem, index) => (
                              <div key={index} className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 last:border-b-0">
                                {(item.label === 'PRODUCTS') ? (
                                  // Product dropdown with description and icon
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                      <Icon name={dropdownItem.icon} size={18} className="text-[#00d690] sm:w-5 sm:h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block btn-animate-rotate"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block btn-animate-rotate"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                ) : dropdownItem.link.startsWith('http') ? (
                                  <a 
                                    href={dropdownItem.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    <span className="block text-sm sm:text-base text-gray-800 font-semibold">
                                      {dropdownItem.title}
                                    </span>
                                    {dropdownItem.description && (
                                      <span className="block mt-1 text-xs sm:text-sm text-gray-600 leading-snug">
                                        {dropdownItem.description}
                                      </span>
                                    )}
                                  </a>
                                ) : (
                                  <Link 
                                    to={dropdownItem.link}
                                    className="block"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    <span className="block text-sm sm:text-base text-gray-800 font-semibold">
                                      {dropdownItem.title}
                                    </span>
                                    {dropdownItem.description && (
                                      <span className="block mt-1 text-xs sm:text-sm text-gray-600 leading-snug">
                                        {dropdownItem.description}
                                      </span>
                                    )}
                                  </Link>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Mobile CTA Button */}
                <div className="px-3 sm:px-4 py-3 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="md"
                    animation="glow"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      window.open('https://cal.com/octalchip/30min', '_blank');
                    }}
                    className="w-full border-primary text-primary bg-white hover:bg-primary hover:text-white whitespace-nowrap"
                  >
                    BOOK FREE CALL
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;